import { useCallback, useState } from 'react';
import type { Offer, OfferDraft } from '@domain/entities';
import type { OfferCreateInput } from '@domain/repositories';
import type { OfferStatus } from '@domain/value-objects';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync, useDebounced, useSelection } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  BulkBar,
  Checkbox,
  ConfirmModal,
  Drawer,
  EmptyState,
  ErrorState,
  FilterPills,
  Modal,
  Pager,
  RefreshButton,
  SearchInput,
  StatusBadge,
  Tag,
  TableSkeleton,
  TextAreaField,
  TextField,
} from '../components/ui';
import { PhotoGalleryUpload } from '../components/photo-gallery-upload';
import { PAGE_SIZE, formatFcfa } from '../theme/tokens';

function PayBadge({ isPaid }: { isPaid: boolean }) {
  return (
    <span
      className="bbm-badge"
      style={{
        background: isPaid ? '#2A6B4722' : '#EEF1F4',
        color: isPaid ? '#2A6B47' : '#8A95A3',
        marginTop: 4,
        display: 'inline-block',
      }}
    >
      {isPaid ? 'Payé' : 'Non payé'}
    </span>
  );
}

type StatusFilter = OfferStatus | 'all';

const FILTERS = [
  { value: 'all' as const, label: 'Toutes' },
  { value: 'pending' as const, label: 'En attente' },
  { value: 'active' as const, label: 'Actives' },
  { value: 'rejected' as const, label: 'Rejetées' },
];


export function OffersPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vOffers = useRealtimeVersion('offers');
  const vOfferCategories = useRealtimeVersion('offer-categories');

  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const [detail, setDetail] = useState<Offer | null>(null);
  const [editing, setEditing] = useState<Offer | null>(null);
  const [draft, setDraft] = useState<OfferDraft>({ title: '', pricing: '', category: '', description: '', expiresAt: null, photoKeys: [] });
  const [saving, setSaving] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<OfferCreateInput>({ businessId: '', title: '', description: '', category: '', pricing: '', photoKeys: [], expiresAt: null, publishNow: false });
  const [createSaving, setCreateSaving] = useState(false);

  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const companiesResource = useAsync(
    () => container.companies.search.execute({ status: 'all', search: '', page: 1, pageSize: 100 }),
    [container],
  );
  const companyOptions = companiesResource.data?.items ?? [];

  const isMobile = useIsMobile();
  const selection = useSelection();

  const formulasResource = useAsync(() => container.settings.listFormulas.execute(), [container]);
  const formulaOptions = formulasResource.data?.filter((f) => f.isActive) ?? [];

  const offerCatsResource = useAsync(() => container.offerCategories.list.execute(), [container, vOfferCategories]);
  const offerCatOptions = offerCatsResource.data?.filter((c) => c.isActive) ?? [];

  const resource = useAsync(
    () =>
      container.offers.search.execute({
        status,
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    [container, status, debouncedSearch, page, vOffers],
  );

  const rows = resource.data?.items ?? [];
  const visibleIds = rows.map((row) => row.id);

  const moderate = useCallback(
    async (ids: readonly string[], next: 'active' | 'rejected') => {
      try {
        await container.offers.moderate.execute({ ids, status: next });
        toast.notify(
          `${ids.length} offre(s) ${next === 'active' ? 'activée(s)' : 'rejetée(s)'} · notification envoyée`,
        );
        selection.clear();
        setDetail(null);
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, selection, resource],
  );

  const suspend = useCallback(
    async (id: string) => {
      try {
        await container.offers.suspend.execute(id);
        toast.notify('Offre suspendue');
        setDetail(null);
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, resource],
  );

  const deleteOffer = useCallback(
    async (id: string) => {
      try {
        await container.offers.delete.execute(id);
        toast.notify('Offre supprimée');
        setDetail(null);
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, resource],
  );

  const openDetail = useCallback(
    async (offer: Offer) => {
      setDetail(offer);
      try {
        const full = await container.offers.get.execute(offer.id);
        setDetail(full);
      } catch {
        // proceed with list data
      }
    },
    [container],
  );

  const openEditor = useCallback(
    async (offer: Offer) => {
      setDetail(null);
      setEditing(offer);
      setDraft({ title: offer.title, pricing: offer.pricing, category: offer.category, description: offer.description, expiresAt: offer.expiresAt, photoKeys: offer.photoKeys });
      try {
        const full = await container.offers.get.execute(offer.id);
        setEditing(full);
        setDraft((d) => ({ ...d, photoKeys: full.photoKeys }));
      } catch {
        // proceed with list data
      }
    },
    [container],
  );

  const createOffer = useCallback(async () => {
    setCreateSaving(true);
    try {
      await container.offers.create.execute(createDraft);
      toast.notify('Offre créée');
      setCreating(false);
      setCreateDraft({ businessId: '', title: '', description: '', category: '', pricing: '', photoKeys: [], expiresAt: null, publishNow: false });
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setCreateSaving(false);
    }
  }, [container, createDraft, toast, resource]);

  const save = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await container.offers.update.execute({ id: editing.id, draft });
      toast.notify('Offre mise à jour');
      setEditing(null);
      const trimmedTitle = draft.title.trim();
      const newPriceAmount = formulaOptions.find((f) => f.slug === draft.pricing)?.amountXof ?? editing.priceAmount;
      resource.patch((page) => ({
        ...page,
        items: page.items.map((o) =>
          o.id === editing.id
            ? { ...o, title: trimmedTitle, category: draft.category, pricing: draft.pricing, priceAmount: newPriceAmount, description: draft.description, expiresAt: draft.expiresAt, photoKeys: draft.photoKeys }
            : o,
        ),
      }));
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editing, draft, toast, resource]);

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <FilterPills
            options={FILTERS}
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(1);
            }}
          />
          <SearchInput
            value={search}
            placeholder="Rechercher une offre…"
            onChange={(next) => {
              setSearch(next);
              setPage(1);
            }}
          />
        </div>
        <div className="bbm-toolbar__end">
          {can('offers', 'create') ? (
            <button
              type="button"
              className="bbm-btn bbm-btn--primary"
              onClick={() => setCreating(true)}
            >
              + Nouvelle offre
            </button>
          ) : null}
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {resource.loading ? <TableSkeleton /> : null}

      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={resource.error.message}
          onRetry={resource.reload}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title="Aucun résultat"
          body="Aucune offre ne correspond à ces filtres."
          actionLabel="Réinitialiser les filtres"
          onAction={() => {
            setStatus('all');
            setSearch('');
            setPage(1);
          }}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length > 0 && resource.data ? (
        <>
          <BulkBar count={selection.selected.length} onClear={selection.clear}>
            <button
              type="button"
              className="bbm-btn bbm-btn--success"
              onClick={() => setPending({
                title: 'Activer la sélection ?',
                body: 'Les offres sélectionnées seront activées.',
                confirmLabel: 'Activer',
                fn: () => void moderate(selection.selected, 'active'),
              })}
            >
              Activer la sélection
            </button>
            <button
              type="button"
              className="bbm-bulkbar__ghost"
              onClick={() => setPending({
                title: 'Rejeter la sélection ?',
                body: 'Les offres sélectionnées seront rejetées.',
                confirmLabel: 'Rejeter',
                danger: true,
                fn: () => void moderate(selection.selected, 'rejected'),
              })}
            >
              Rejeter la sélection
            </button>
            <button
              type="button"
              className="bbm-bulkbar__ghost"
              disabled={!can('offers', 'delete')}
              onClick={() => setPending({
                title: 'Suspendre la sélection ?',
                body: 'Les offres sélectionnées seront suspendues.',
                confirmLabel: 'Suspendre',
                danger: true,
                fn: () => {
                  for (const id of selection.selected) void suspend(id);
                },
              })}
            >
              Suspendre la sélection
            </button>
            <button
              type="button"
              className="bbm-bulkbar__ghost"
              disabled={!can('offers', 'delete')}
              onClick={() => setPending({
                title: `Supprimer ${selection.selected.length} offre(s) ?`,
                body: 'Cette action est irréversible. Les offres seront définitivement supprimées.',
                confirmLabel: 'Supprimer',
                danger: true,
                fn: () => {
                  for (const id of selection.selected) void deleteOffer(id);
                },
              })}
            >
              Supprimer la sélection
            </button>
          </BulkBar>

          {isMobile ? (
            <div className="bbm-card-stack">
              {rows.map((offer) => (
                <div key={offer.id} className="bbm-mobile-card">
                  <div className="bbm-mobile-card__head">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={selection.isSelected(offer.id)}
                        onChange={() => selection.toggle(offer.id)}
                        label={`Sélectionner ${offer.title}`}
                      />
                      {offer.photoUrls[0] ? (
                        <img src={offer.photoUrls[0]} alt="" aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 8, background: '#EEF1F4', flexShrink: 0 }} />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div className="bbm-mobile-card__title">{offer.title}</div>
                        <div className="bbm-mobile-card__sub">{offer.company}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <StatusBadge status={offer.status} />
                      <PayBadge isPaid={offer.isPaid} />
                    </div>
                  </div>
                  <div className="bbm-mobile-card__fields">
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Catég.</span>
                      <span>{offer.category}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Prix</span>
                      <span>{formatFcfa(offer.priceAmount)}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Expire</span>
                      <span>{offer.expiresAt ? new Date(offer.expiresAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                    </div>
                  </div>
                  <div className="bbm-mobile-card__actions">
                    {offer.status === 'pending' ? (
                      <>
                        <button type="button" className="bbm-btn bbm-btn--success" onClick={() => setPending({
                          title: 'Approuver cette offre ?',
                          body: 'Le statut passera à Actif.',
                          confirmLabel: 'Approuver',
                          fn: () => void moderate([offer.id], 'active'),
                        })}>Approuver</button>
                        <button type="button" className="bbm-btn bbm-btn--danger" onClick={() => setPending({
                          title: 'Rejeter cette offre ?',
                          body: 'Le statut passera à Rejetée.',
                          confirmLabel: 'Rejeter',
                          danger: true,
                          fn: () => void moderate([offer.id], 'rejected'),
                        })}>Rejeter</button>
                      </>
                    ) : offer.status === 'active' ? (
                      <button type="button" className="bbm-btn bbm-btn--warn" disabled={!can('offers', 'delete')} onClick={() => setPending({
                        title: 'Suspendre cette offre ?',
                        body: 'Les offres sélectionnées seront suspendues.',
                        confirmLabel: 'Suspendre',
                        danger: true,
                        fn: () => void suspend(offer.id),
                      })}>Suspendre</button>
                    ) : null}
                    <button type="button" className="bbm-btn" onClick={() => void openDetail(offer)}>Voir</button>
                    <button type="button" className="bbm-btn" disabled={!can('offers', 'edit')} onClick={() => void openEditor(offer)}>Modifier</button>
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--danger"
                      disabled={!can('offers', 'delete')}
                      onClick={() => setPending({
                        title: 'Supprimer cette offre ?',
                        body: 'Cette action est irréversible.',
                        confirmLabel: 'Supprimer',
                        danger: true,
                        fn: () => void deleteOffer(offer.id),
                      })}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bbm-panel">
              <div className="bbm-row bbm-row--head">
                <Checkbox
                  checked={selection.allSelected(visibleIds)}
                  onChange={() => selection.toggleAll(visibleIds)}
                  label="Tout sélectionner"
                />
                <div style={{ width: 44 }}>Photo</div>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>Offre · Propriétaire</div>
                <div style={{ width: 95 }}>Catég. · Prix</div>
                <div style={{ width: 150 }}>Engagement</div>
                <div style={{ width: 85 }}>Expire</div>
                <div style={{ width: 110 }}>Statut</div>
                <div style={{ width: 360, flexShrink: 0, textAlign: 'right', borderLeft: '1px solid transparent', paddingLeft: 20, marginLeft: 4 }}>Actions</div>
              </div>

              {rows.map((offer) => (
                <div key={offer.id} className="bbm-row">
                  <Checkbox
                    checked={selection.isSelected(offer.id)}
                    onChange={() => selection.toggle(offer.id)}
                    label={`Sélectionner ${offer.title}`}
                  />
                  <div
                    aria-hidden="true"
                    style={{
                      width: 44, height: 36, borderRadius: 8, flexShrink: 0,
                      background: offer.photoUrls[0] ? `url(${offer.photoUrls[0]}) center/cover` : '#EEF1F4',
                    }}
                  />
                  {/* Titre + Propriétaire — flex avec min-width:0 pour tronquer */}
                  <button
                    type="button"
                    onClick={() => void openDetail(offer)}
                    style={{ flex: '1 1 0', minWidth: 0, textAlign: 'left', border: 'none', background: 'none', padding: 0, overflow: 'hidden' }}
                  >
                    <div className="bbm-cell-strong bbm-truncate">{offer.title}</div>
                    <div className="bbm-cell-sub bbm-truncate">
                      {offer.company}
                      <span style={{ marginLeft: 5, fontWeight: 700, color: offer.ownerType === 'business' ? '#33506B' : '#8A5B22' }}>
                        · {offer.ownerType === 'business' ? 'Entreprise' : 'Particulier'}
                      </span>
                    </div>
                  </button>
                  {/* Catégorie + Prix */}
                  <div style={{ width: 95, flexShrink: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#33506B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={offer.category}>
                      {offer.category}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#2A6B47', marginTop: 2 }}>
                      {formatFcfa(offer.priceAmount)}
                    </div>
                  </div>
                  {/* Engagement */}
                  <div style={{ width: 150, flexShrink: 0, fontSize: 12 }}>
                    <div>
                      <span style={{ color: '#33506B', fontWeight: 700 }}>{offer.viewsCount.toLocaleString('fr-FR')}</span>
                      <span style={{ color: '#8A95A3' }}> vues · </span>
                      <span style={{ color: '#5CA989', fontWeight: 700 }}>{offer.callClicksCount}</span>
                      <span style={{ color: '#8A95A3' }}> app.</span>
                    </div>
                    <div style={{ marginTop: 2 }}>
                      <span style={{ color: '#128C7E', fontWeight: 700 }}>{offer.whatsappClicksCount}</span>
                      <span style={{ color: '#8A95A3' }}> WA · </span>
                      <span style={{ color: '#E0A552', fontWeight: 700 }}>{offer.emailClicksCount}</span>
                      <span style={{ color: '#8A95A3' }}> mail</span>
                    </div>
                  </div>
                  {/* Expire */}
                  <div style={{ width: 85, flexShrink: 0, fontSize: 12, color: '#4A5560' }}>
                    {offer.expiresAt
                      ? new Date(offer.expiresAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'}
                  </div>
                  {/* Statut */}
                  <div style={{ width: 110, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <StatusBadge status={offer.status} />
                    <PayBadge isPaid={offer.isPaid} />
                  </div>
                  {/* Actions */}
                  <div style={{ width: 360, flexShrink: 0, display: 'flex', gap: 6, justifyContent: 'flex-end', borderLeft: '1px solid #E4E8ED', paddingLeft: 20, marginLeft: 4 }}>
                    {offer.status === 'pending' ? (
                      <>
                        <button
                          type="button"
                          className="bbm-btn bbm-btn--success"
                          onClick={() => setPending({
                            title: 'Approuver cette offre ?',
                            body: 'Le statut passera à Actif.',
                            confirmLabel: 'Approuver',
                            fn: () => void moderate([offer.id], 'active'),
                          })}
                        >
                          Approuver
                        </button>
                        <button
                          type="button"
                          className="bbm-btn bbm-btn--danger"
                          onClick={() => setPending({
                            title: 'Rejeter cette offre ?',
                            body: 'Le statut passera à Rejetée.',
                            confirmLabel: 'Rejeter',
                            danger: true,
                            fn: () => void moderate([offer.id], 'rejected'),
                          })}
                        >
                          Rejeter
                        </button>
                      </>
                    ) : offer.status === 'active' ? (
                      <button
                        type="button"
                        className="bbm-btn bbm-btn--warn"
                        disabled={!can('offers', 'delete')}
                        onClick={() => setPending({
                          title: 'Suspendre cette offre ?',
                          body: 'Les offres sélectionnées seront suspendues.',
                          confirmLabel: 'Suspendre',
                          danger: true,
                          fn: () => void suspend(offer.id),
                        })}
                      >
                        Suspendre
                      </button>
                    ) : null}
                    <button type="button" className="bbm-btn" onClick={() => void openDetail(offer)}>Voir</button>
                    <button type="button" className="bbm-btn" disabled={!can('offers', 'edit')} onClick={() => void openEditor(offer)}>Modifier</button>
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--danger"
                      disabled={!can('offers', 'delete')}
                      onClick={() => setPending({
                        title: 'Supprimer cette offre ?',
                        body: 'Cette action est irréversible.',
                        confirmLabel: 'Supprimer',
                        danger: true,
                        fn: () => void deleteOffer(offer.id),
                      })}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pager
            page={resource.data.page}
            pageCount={resource.data.pageCount}
            total={resource.data.total}
            pageSize={resource.data.pageSize}
            onChange={setPage}
          />
        </>
      ) : null}

      {detail ? (
        <Drawer title={detail.title} subtitle={detail.company} onClose={() => setDetail(null)}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge status={detail.status} />
            <PayBadge isPaid={detail.isPaid} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
            {detail.photoUrls.map((url, i) => (
              <div
                key={url || i}
                aria-hidden="true"
                style={{
                  width: 90,
                  height: 64,
                  borderRadius: 10,
                  background: `url(${url}) center/cover`,
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div className="bbm-detail-label">Catégorie</div>
                <Tag background="#7FB3D522" color="#33506B">{detail.category}</Tag>
              </div>
              <div>
                <div className="bbm-detail-label">Formule tarifaire</div>
                <Tag background="#E0A55222" color="#8A5B22">{detail.pricing}</Tag>
              </div>
              <div>
                <div className="bbm-detail-label">Prix</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#2A6B47' }}>
                  {formatFcfa(detail.priceAmount)}
                </div>
              </div>
              <div>
                <div className="bbm-detail-label">Type</div>
                <Tag background="#F0F2F5" color="#6B7785">
                  {detail.ownerType === 'business' ? 'Entreprise' : 'Particulier'}
                </Tag>
              </div>
            </div>
            <div>
              <div className="bbm-detail-label">Description</div>
              <div style={{ fontSize: 13.5, color: '#4A5560', lineHeight: 1.55 }}>
                {detail.description}
              </div>
            </div>
            <div>
              <div className="bbm-detail-label" style={{ marginBottom: 8 }}>Statistiques</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[
                  { label: 'Vues', value: detail.viewsCount, color: '#33506B' },
                  { label: 'Appels', value: detail.callClicksCount, color: '#5CA989' },
                  { label: 'WhatsApp', value: detail.whatsappClicksCount, color: '#25D366' },
                  { label: 'Emails', value: detail.emailClicksCount, color: '#E0A552' },
                ].map((s) => (
                  <div key={s.label} style={{ background: '#F8F9FA', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: '#8A95A3', fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div className="bbm-detail-label">Publiée le</div>
                <div className="bbm-detail-value">
                  {detail.publishedAt
                    ? new Date(detail.publishedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
                </div>
              </div>
              <div>
                <div className="bbm-detail-label">Expire le</div>
                <div className="bbm-detail-value">
                  {detail.expiresAt
                    ? new Date(detail.expiresAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
                </div>
              </div>
              <div>
                <div className="bbm-detail-label">Créée le</div>
                <div className="bbm-detail-value">
                  {new Date(detail.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            </div>
          </div>
          {detail.status === 'pending' ? (
            <div style={{ display: 'flex', gap: 10, marginTop: 26 }}>
              <button
                type="button"
                className="bbm-btn bbm-btn--success bbm-btn--block"
                onClick={() => setPending({
                  title: 'Approuver cette offre ?',
                  body: 'Le statut passera à Actif.',
                  confirmLabel: 'Approuver',
                  fn: () => void moderate([detail.id], 'active'),
                })}
              >
                Approuver
              </button>
              <button
                type="button"
                className="bbm-btn bbm-btn--danger bbm-btn--block"
                onClick={() => setPending({
                  title: 'Rejeter cette offre ?',
                  body: 'Le statut passera à Rejetée.',
                  confirmLabel: 'Rejeter',
                  danger: true,
                  fn: () => void moderate([detail.id], 'rejected'),
                })}
              >
                Rejeter
              </button>
            </div>
          ) : null}
          {detail.status === 'active' ? (
            <button
              type="button"
              className="bbm-btn bbm-btn--warn bbm-btn--block"
              style={{ marginTop: 10 }}
              disabled={!can('offers', 'delete')}
              title={can('offers', 'delete') ? '' : 'Réservé au super-admin'}
              onClick={() => setPending({
                title: 'Suspendre cette offre ?',
                body: 'Les offres sélectionnées seront suspendues.',
                confirmLabel: 'Suspendre',
                danger: true,
                fn: () => void suspend(detail.id),
              })}
            >
              Suspendre l'offre
            </button>
          ) : null}
          <button
            type="button"
            className="bbm-btn bbm-btn--block"
            style={{ marginTop: 10 }}
            disabled={!can('offers', 'edit')}
            onClick={() => void openEditor(detail)}
          >
            Modifier l'offre
          </button>
          <button
            type="button"
            className="bbm-btn bbm-btn--danger bbm-btn--block"
            style={{ marginTop: 10 }}
            disabled={!can('offers', 'delete')}
            title={can('offers', 'delete') ? '' : 'Réservé au super-admin'}
            onClick={() => setPending({
              title: 'Supprimer cette offre ?',
              body: 'Cette action est irréversible. L\'offre sera définitivement supprimée.',
              confirmLabel: 'Supprimer',
              danger: true,
              fn: () => void deleteOffer(detail.id),
            })}
          >
            Supprimer l'offre
          </button>
        </Drawer>
      ) : null}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {creating ? (
        <Modal
          title="Nouvelle offre"
          onClose={() => setCreating(false)}
          onSubmit={() => void createOffer()}
          busy={createSaving}
        >
          {/* Propriétaire */}
          <div style={{ marginBottom: 12 }}>
            <label className="bbm-label">Propriétaire (entreprise)</label>
            <select
              className="bbm-input"
              style={{ width: '100%' }}
              disabled={companiesResource.loading}
              value={createDraft.businessId}
              onChange={(e) => setCreateDraft({ ...createDraft, businessId: e.target.value })}
            >
              <option value="">
                {companiesResource.loading
                  ? 'Chargement…'
                  : companiesResource.error
                    ? `Erreur : ${companiesResource.error.message}`
                    : `— Sélectionner une entreprise (${companyOptions.length}) —`}
              </option>
              {companyOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name} · {c.status === 'approved' ? '✓' : c.status === 'pending' ? 'en attente' : 'rejeté'}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 4 }}>
            <div>
              <label className="bbm-label">Catégorie</label>
              <select
                className="bbm-input"
                style={{ width: '100%' }}
                value={createDraft.category}
                onChange={(e) => setCreateDraft({ ...createDraft, category: e.target.value })}
              >
                <option value="">— Sélectionner —</option>
                {offerCatOptions.map((c) => (
                  <option key={c.id} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="bbm-label">Formule tarifaire</label>
              <select
                className="bbm-input"
                style={{ width: '100%' }}
                value={createDraft.pricing}
                onChange={(e) => setCreateDraft({ ...createDraft, pricing: e.target.value })}
              >
                <option value="">— Sélectionner —</option>
                {formulaOptions.map((f) => (
                  <option key={f.slug} value={f.slug}>
                    {f.label} — {f.amountXof.toLocaleString('fr-FR')} FCFA
                  </option>
                ))}
              </select>
            </div>
          </div>

          <TextField
            label="Titre"
            value={createDraft.title}
            onChange={(value) => setCreateDraft({ ...createDraft, title: value })}
          />

          <div style={{ marginBottom: 12 }}>
            <label className="bbm-label">Date d'expiration</label>
            <input
              type="date"
              className="bbm-field"
              style={{ width: '100%' }}
              value={createDraft.expiresAt ? createDraft.expiresAt.slice(0, 10) : ''}
              onChange={(e) =>
                setCreateDraft({ ...createDraft, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
            />
          </div>

          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              id="create-publish-now"
              type="checkbox"
              checked={createDraft.publishNow}
              onChange={(e) => setCreateDraft({ ...createDraft, publishNow: e.target.checked })}
            />
            <label htmlFor="create-publish-now" style={{ fontSize: 13.5, color: '#4A5560', cursor: 'pointer' }}>
              Publier immédiatement (sinon : en attente de modération)
            </label>
          </div>

          <span className="bbm-label">Photos</span>
          <PhotoGalleryUpload
            key="create-offer"
            initialKeys={[]}
            initialUrls={[]}
            purpose="offer-photo"
            onChange={(keys) => setCreateDraft((d) => ({ ...d, photoKeys: keys }))}
            onRequestUpload={container.media.requestUpload}
          />

          <TextAreaField
            label="Description"
            rows={4}
            value={createDraft.description}
            onChange={(value) => setCreateDraft({ ...createDraft, description: value })}
          />
        </Modal>
      ) : null}

      {editing ? (
        <Modal
          title="Modifier l'offre"
          onClose={() => setEditing(null)}
          onSubmit={() => void save()}
          busy={saving}
        >
          {/* Propriétaire — lecture seule */}
          <div style={{ background: '#F8F9FA', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8A95A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Propriétaire</div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#33506B', marginTop: 2 }}>{editing.company}</div>
            </div>
            <span
              className="bbm-badge"
              style={{ background: editing.ownerType === 'business' ? '#33506B22' : '#E0A55222', color: editing.ownerType === 'business' ? '#33506B' : '#8A5B22', marginLeft: 'auto' }}
            >
              {editing.ownerType === 'business' ? 'Entreprise' : 'Particulier'}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 4 }}>
            <div>
              <label className="bbm-label">Catégorie</label>
              <select
                className="bbm-input"
                style={{ width: '100%' }}
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              >
                <option value="">— Sélectionner —</option>
                {offerCatOptions.map((c) => (
                  <option key={c.id} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="bbm-label">Formule tarifaire</label>
              <select
                className="bbm-input"
                style={{ width: '100%' }}
                value={draft.pricing}
                onChange={(e) => setDraft({ ...draft, pricing: e.target.value })}
              >
                <option value="">— Sélectionner —</option>
                {formulaOptions.map((f) => (
                  <option key={f.slug} value={f.slug}>
                    {f.label} — {f.amountXof.toLocaleString('fr-FR')} FCFA
                  </option>
                ))}
              </select>
            </div>
          </div>

          <TextField
            label="Titre"
            value={draft.title}
            onChange={(value) => setDraft({ ...draft, title: value })}
          />

          <div style={{ marginBottom: 12 }}>
            <label className="bbm-label">Date d'expiration</label>
            <input
              type="date"
              className="bbm-field"
              style={{ width: '100%' }}
              value={draft.expiresAt ? draft.expiresAt.slice(0, 10) : ''}
              onChange={(e) =>
                setDraft({ ...draft, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
            />
          </div>

          <span className="bbm-label">Photos</span>
          <PhotoGalleryUpload
            key={editing?.id ?? 'new'}
            initialKeys={draft.photoKeys}
            initialUrls={editing?.photoUrls ?? []}
            purpose="offer-photo"
            onChange={(keys) => setDraft((d) => ({ ...d, photoKeys: keys }))}
            onRequestUpload={container.media.requestUpload}
          />
          <TextAreaField
            label="Description"
            rows={4}
            value={draft.description}
            onChange={(value) => setDraft({ ...draft, description: value })}
          />
        </Modal>
      ) : null}
    </>
  );
}
